<template>
    <NuxtLayout name="bio-tools" :favorite="true">
        <div class="bio-tools-result container" ref="container">
            <section class="title">
                <div class="result-title font-bold">{{ $t("BioTools.SignalPath.Result") }}</div>
                <div class="result-label">({{ species }}, {{ filename }})</div>
            </section>

            <section class="content">
                <DefaultSearch class="search-input" v-model="pagination.query" :width="320" @input="onInput" style="margin: 0 0 32px" />

                <UiTable2 class="table-list" :option="tableOptions" :data="currentPageData" style="margin: 38px 0px 0px" @sort="triggerSort">
                    <template #p_values_explain="{ data }">
                        <div style="width: 240px; white-space: pre-wrap; line-height: 22px">
                            {{ data.explain }}
                        </div>
                    </template>
                    <template #FDR_explain="{ data }">
                        <div style="width: 240px; white-space: pre-wrap; line-height: 22px">
                            {{ data.explain }}
                        </div>
                    </template>
                    <template #MES_explain="{ data }">
                        <div style="width: 240px; white-space: pre-wrap; line-height: 22px">
                            {{ data.explain }}
                        </div>
                    </template>

                    <template #descriptions="{ data }">
                        <div :class="css_normalize.assets" class="ellipsis" style="width: 452px" @click="toDeatil(data)">{{ data.descriptions }}</div>
                    </template>
                </UiTable2>

                <UiPagination v-model="pagination.page" v-bind="pagination" style="margin: 20px 0px 0px">
                    <template #items> {{ $t("Layout.PaginationDetails", { count: pagination.count, limit: 10 }) }}</template>
                </UiPagination>
            </section>
        </div>
    </NuxtLayout>
</template>
<script lang="ts" setup>
import { UiTable2, type UiTableOption2, $message, $loading, UiPagination } from "various-ui";
import { Formats } from "@/assets/js";
import { DefaultSearch } from "@/components/controls";

//* 样式
import css_normalize from "@/assets/style/src/index.module.less";

const route = useRoute(); // 路由配置
const i18n = useI18n(); //语言配置
let timer: NodeJS.Timeout | undefined; // 定时器

const species = ref("");
const filename = ref("");

/**表格数据 */
const tableData = ref([]);
const pagination = ref({ page: 1, count: 0, limit: 10, page_count: 0, sort: "", order: "", query: "" }); //分页配置

/**表格配置 */
const tableOptions: UiTableOption2[] = [
    {
        name: i18n.t("BioTools.SignalPath.PathwayId"),
        key: "pathway_ids",
        width: 224,
    },
    {
        name: i18n.t("Description"),
        key: "descriptions",
        width: 540,
        sort: true,
        align: "center",
    },
    {
        name: "P value",
        key: "p_values",
        width: 180,
        sort: true,
        explain: i18n.t("BioTools.SignalPath.PValueDesc"),
    },
    {
        name: "FDR",
        key: "FDR",
        width: 180,
        sort: true,
        explain: i18n.t("BioTools.SignalPath.FDRDesc"),
    },
    {
        name: "MES",
        key: "MES",
        width: 180,
        sort: true,
        explain: i18n.t("BioTools.SignalPath.MESDesc"),
    },
];

/**@name 表格数据 */
const currentData = computed(() => {
    const { sort, order, query } = pagination.value;
    const data = Formats.filter(tableData.value || [], sort, order, query, ["pathway_ids", "descriptions", "p_values", "FDR", "MES"]);
    return data || [];
});

/**@name 表格当前Page的数据 */
const currentPageData = computed(() => {
    const { page, limit } = pagination.value;
    return currentData.value.slice((page - 1) * limit, page * limit);
});

/**@name 初始化函数 */
const init = (data: any) => {
    filename.value = data.filename || "";
    species.value = data.species || "";
    tableData.value = data.result || "";
    pagination.value.count = currentData.value.length;
    pagination.value.page_count = Math.ceil(pagination.value.count / pagination.value.limit);
};

/**@name 关键词搜索响应函数 */
const onInput = () => {
    pagination.value.page = 1;
    pagination.value.count = currentData.value.length;
    pagination.value.page_count = Math.ceil(pagination.value.count / pagination.value.limit);
};

/**@name 切换排序字段 */
const triggerSort = (data: { key: string; value: string }): void => {
    pagination.value.sort = data?.key || "";
    pagination.value.order = data?.value || "";
};

const toDeatil = (data: any) => {
    open(routerResolve({ name: "SignalPathDetail", query: { has_id: data.has_id, job_id: data.job_id } }).href as string, "_blank");
};

onMounted(async () => {
    const uuid = Formats.query(route.query.uuid);
    if (uuid) {
        $loading.open({ message: "" });
        await api.frontend("/api/v1/bio-tools/signal-path/getList", {
            method: "POST",
            data: { job_id: uuid },
            success: (response) => {
                $loading.close();
                init(response.data);
            },
        });
    } else {
        $message.error(i18n.t("BioTools.NoResult"));
        timer = setInterval(() => {
            routerPush({ name: "SignalPath" });
        }, 2000);
    }
});
</script>

<style lang="less" scoped>
.bio-tools-result {
    margin: 44px auto 0;
    position: relative;

    section.title {
        display: flex;
        align-items: flex-end;
        margin: 0 0 42px;
    }

    .result-title {
        font-size: 36px;
        line-height: 64px;
        color: var(--uicolor-font-1);
    }

    .result-label {
        color: #666;
        margin: 0 0 8px 8px;
        line-height: 28px;
    }

    .search-input {
        width: 320px;
    }
}
</style>
